import { AI_LOADING_MESSAGES } from './globalConstants'
import {
  ERROR_CODES,
  ERROR_MESSAGES,
  FRAMEWORK_MODEL_IDS,
  MIME_TYPE,
  APPLE_FILE_EXTENSIONS,
} from './globalEnums'
import { isGrpcError } from './isGrpcError'

export const isFullySupportedFramework = (
  frameworkModelId: FRAMEWORK_MODEL_IDS,
): boolean => {
  return (
    frameworkModelId === FRAMEWORK_MODEL_IDS.ISO27001 ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.SOC2 ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.HIPAA ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.PCISAQD ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.PCISAQA ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.HITRUST ||
    frameworkModelId === FRAMEWORK_MODEL_IDS.NISTCSF
  )
}

export const isErrorType = (err: unknown, type: ERROR_CODES): boolean =>
  isGrpcError(err) && err.message.includes(type)

export const getErrorCode = (message: string): ERROR_CODES | undefined => {
  const errorCode = Object.values(ERROR_CODES).find((code) =>
    message.includes(code),
  )
  return errorCode
}

export type ErrorMessageResponse = {
  message: string
  shouldLog: boolean
}

export const getErrorMessage = (err: unknown): ErrorMessageResponse => {
  if (!isGrpcError(err)) {
    return {
      message: ERROR_MESSAGES.UNKNOWN,
      shouldLog: true,
    }
  }

  const errorMessageCode = getErrorCode(err.message)
  switch (errorMessageCode) {
    case ERROR_CODES.DUPLICATE_ENTRY:
      return {
        message: ERROR_MESSAGES[ERROR_CODES.DUPLICATE_ENTRY],
        shouldLog: false,
      }
    case ERROR_CODES.TOO_LONG:
      return {
        message: ERROR_MESSAGES[ERROR_CODES.TOO_LONG],
        shouldLog: false,
      }
    default:
      return {
        message: ERROR_MESSAGES.UNKNOWN,
        shouldLog: true,
      }
  }
}

export const getPercentageCompleted = (
  completed: number,
  total: number,
): number => {
  return total > 0 ? Math.round((completed / total) * 100) : 0
}

export const openInNewTab = (href = ''): void => {
  if (!href.length) return
  Object.assign(document.createElement('a'), {
    target: '_blank',
    rel: 'noopener noreferrer',
    href: href,
  }).click()
}

export const getMimeTypeFromExtension = (name: string): MIME_TYPE => {
  const splitName = name.split('.')
  const extension = splitName[splitName.length - 1]
  switch (extension) {
    case APPLE_FILE_EXTENSIONS.KEY:
      return MIME_TYPE.APPLE_KEYNOTE
    case APPLE_FILE_EXTENSIONS.NUMBERS:
      return MIME_TYPE.APPLE_NUMBERS
    case APPLE_FILE_EXTENSIONS.PAGES:
      return MIME_TYPE.APPLE_PAGES
    default:
      return MIME_TYPE.NULL
  }
}

type messageWithId = {
  getId: () => string
}

export const normalizeById = <T extends messageWithId>(
  messageList: T[],
): Record<string, T> =>
  messageList.reduce((acc, message): Record<string, T> => {
    const controlId = message.getId()
    acc[controlId] = message
    return acc
  }, {} as Record<string, T>)

type messageWithControlId = {
  getControlId: () => string
}

export const normalizeByControlId = <T extends messageWithControlId>(
  messageList: T[],
): Record<string, T> =>
  messageList.reduce((acc, message): Record<string, T> => {
    const controlId = message.getControlId()
    acc[controlId] = message
    return acc
  }, {} as Record<string, T>)

export const getPluralization = (word: string, count: number): string =>
  count === 1 ? word : `${word}s`

// Function to capitalize the first letter of each word
export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const isJSON = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

export const getFileSize = (file: File): number => {
  // Plus sign drops unnecessary zeros at end of number
  return +(file.size / 1000).toFixed(2)
}

export const getAILoadingMessage = (): string => {
  const index = Math.floor(Math.random() * AI_LOADING_MESSAGES.length)
  return AI_LOADING_MESSAGES[index] || AI_LOADING_MESSAGES[0]
}
